import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { SLICE_PER_BREAD } from '../../../../constants/currencies';
import { Icons, IconType } from '../../../../constants/icons';
import { getDonationImage } from '../../../../constants/images';
import { SVGIconType } from '../../../../constants/svgIcon';
import { getWebBoosterVairant } from '../../../../constants/webBoosterType';
import { AlbumPartition, Space } from '../../../../models/SpaceStore';
import { Profile, WebBoosterType } from '../../../../models/UserStore';
import {
  AIExamMemoryBoosterBanner,
  AlbumNodeProps,
  AvatarNode,
  BreadBar,
  CurrencyAmount,
  CurrencyNode,
  FolderBreadcrumbRow,
  FolderBreadcrumbVariant,
  PngIcon,
  SpaceNode,
  SpaceNodeProps,
  SvgIcon,
} from '../../../Molecules';
import {
  AllHorizontalRow,
  FolderGameSetRow,
  HomeClubCardGroup,
} from '../../../Organisms';

import styles from './AppMainTemplate.module.css';

export type AppMainTemplateProps = {
  profile: Profile | null | undefined;
  breadSlice: number;
  currencies: CurrencyAmount[];
  webBoosterType: WebBoosterType | undefined;
  breadcrumbs: AlbumPartition[];
  albumData?: AlbumNodeProps[];
  hasRewardBox: boolean;
  recentSpacesData: SpaceNodeProps[];
  hotSpaces: Space[];
  donationImageSet: number;
  isAddHomeButtonOnTop: boolean;
  onClickTrialRegister: () => void;
  onClickAccountSetting: () => void;
  onClickHomeClub: () => void;
  onClickCurrency: () => void;
  onClickScrollBread: () => void;
  onClickBoostVideo: () => void;
  onClickSwitchBooster: () => void;
  onClickBreadcrumb: () => void;
  onClickCreateGames: () => void;
  onClickRevisionDetail: () => void;
  onShareLink: () => void;
  onClickRecentSpaces: () => void;
  onSeeAllHotClubs: () => void;
  onClickClub: (classroomId: string) => void;
  onClickBreadDonated: () => void;
  onClickGetMoreBread: () => void;
  onClickDonateBread: () => void;
  onClickShare: () => void;
  onClickAddToHomeScreen: () => void;
};

export const AppMainTemplate = memo((props: AppMainTemplateProps) => {
  const {
    profile,
    breadSlice,
    currencies,
    webBoosterType,
    breadcrumbs,
    albumData,
    hasRewardBox,
    recentSpacesData,
    // hotSpaces,
    donationImageSet,
    isAddHomeButtonOnTop,
    onClickTrialRegister,
    onClickAccountSetting,
    onClickHomeClub,
    onClickCurrency,
    onClickScrollBread,
    onClickBoostVideo,
    onClickSwitchBooster,
    onClickBreadcrumb,
    onClickCreateGames,
    onClickRevisionDetail,
    onShareLink,
    onClickRecentSpaces,
    onSeeAllHotClubs,
    // onClickClub,
    onClickBreadDonated,
    onClickGetMoreBread,
    onClickDonateBread,
    onClickShare,
    onClickAddToHomeScreen,
  } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.appMain}>
      {profile?.isTrialUser && (
        <div className={styles.trialRegisterBar} onClick={onClickTrialRegister}>
          <SvgIcon
            name={SVGIconType.Register}
            width={36}
            height={36}
            color="#05656B"
          />
          <div className={styles.trialRegisterText}>
            <p>{t('This is a Trial Account.')}</p>
            <p className={styles.trialRegisterGreenBoldText}>
              {t('Register to get FREE Basic Plan!')}
            </p>
          </div>
          <SvgIcon name={SVGIconType.Arrow} width={16} height={16} />
        </div>
      )}
      <div className={styles.centeredContainer}>
        <div className={styles.avoidGap}>
          {profile && (
            <div
              className={styles.vContainer}
              style={{ marginBottom: '0.8rem' }}
            >
              <div
                className={`${styles.hContainer} ${styles.hoverEffect} ${styles.minorPadding}`}
                onClick={onClickAccountSetting}
              >
                <div className={`${styles.vContainerMinWidth}`}>
                  <AvatarNode profile={profile} size={80} />
                </div>
                <div className={`${styles.vContainer}`} style={{ gap: '4px' }}>
                  <div className={styles.hContainer}>
                    <div
                      className={styles.boldName}
                      style={{ textAlign: 'left', width: '100%' }}
                    >
                      {profile.name}
                    </div>
                  </div>
                  <div className={styles.assetArea}>
                    <div className={styles.expInfo}>
                      <div className={styles.barContainer}>
                        <div
                          className={styles.blackLayerText}
                        >{`Lv.${profile.exp.level}`}</div>
                        <div className={styles.progressBar}>
                          <div
                            className={styles.progress}
                            style={{ width: `${profile.exp.progress * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={styles.hContainer}
                style={{ padding: '5px 10px' }}
              >
                <div
                  className={`${styles.breadButton} ${styles.hoverStyle}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickScrollBread();
                  }}
                >
                  <div className={styles.currencyItem}>
                    <PngIcon name={'Bread'} width={24} height={24} />
                    {numbro(Math.floor(breadSlice / SLICE_PER_BREAD)).format({
                      thousandSeparated: true,
                    })}
                  </div>
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    color="#808080"
                    width={16}
                    height={16}
                  />
                </div>
                <div
                  className={`${styles.assetContainer}  ${styles.hoverStyle}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickCurrency();
                  }}
                >
                  {currencies.map(({ type, amount }) => (
                    <CurrencyNode
                      key={type}
                      currencyType={type}
                      amount={amount}
                      iconSize={22}
                      withBox={false}
                      isPaying={false}
                      textStyle={{ fontSize: '0.9rem' }}
                    />
                  ))}
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    color="#808080"
                    width={16}
                    height={16}
                  />
                </div>
              </div>
            </div>
          )}

          <div
            className={
              webBoosterType === WebBoosterType.Normal
                ? styles.greenBorderContainer
                : styles.brownBorderContainer
            }
          >
            <div className={styles.boosterBannerContainer}>
              <AIExamMemoryBoosterBanner
                onClickBoostVideo={onClickBoostVideo}
                onClickSwitchBooster={onClickSwitchBooster}
                webBoosterType={webBoosterType}
              />
            </div>

            <div className={styles.allContentRow}>
              <FolderBreadcrumbRow
                webBoosterType={webBoosterType}
                breadcrumbs={breadcrumbs}
                variant={FolderBreadcrumbVariant.White}
                onClickBreadcrumb={onClickBreadcrumb}
              />
            </div>

            <div className={styles.orangeContainerVariant}>
              <p className={styles.orangeText}>
                {t(getWebBoosterVairant(webBoosterType).practiceName ?? '')}
              </p>
              <hr style={{ margin: '8px 0' }} />
              <p className={styles.neverForgetText}>
                {t(getWebBoosterVairant(webBoosterType).neverForgetText)}
              </p>
              <div
                className={styles.textBoxCreate}
                onClick={onClickCreateGames}
              >
                {t(getWebBoosterVairant(webBoosterType).textToPractice)}
              </div>
              {albumData && albumData.length > 0 ? (
                <FolderGameSetRow
                  albumData={albumData}
                  onClickArrow={onClickHomeClub}
                />
              ) : null}

              {hasRewardBox && (
                <div className={styles.rewardBoxContainer}>
                  <div className={styles.rewardBoxTriangle} />
                  <div
                    className={styles.rewardBoxContent}
                    onClick={onClickCreateGames}
                  >
                    <img
                      src={Icons[IconType.RewardBox]}
                      className={styles.rewardBoxIcon}
                    />
                    <div className={styles.orangeBoxText}>
                      {t('Create Memory Practice to collect rewards!')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.homeClubCardRow}>
              <HomeClubCardGroup
                onClickRevisionDetail={onClickRevisionDetail}
              />
            </div>

            <div
              className={`${styles.hContainerWithPadding} ${styles.hoverStyle}`}
              onClick={onShareLink}
            >
              <SvgIcon
                name={SVGIconType.Share}
                width={25}
                height={25}
                color={'#00a698'}
              />
              <div className={styles.greenText}>
                {t(getWebBoosterVairant(webBoosterType).shareBooster)}
              </div>
            </div>
          </div>
        </div>
        {/* 用户未点击按钮时显示在该位置 */}
        {isAddHomeButtonOnTop && (
          <div
            className={`${styles.addToHomeScreenButton}`}
            onClick={onClickAddToHomeScreen}
          >
            <img
              className={styles.joyajoyicon}
              src={Icons[IconType.JoyajoyIcon]}
              sizes="24px"
            />
            <span>{t('Add Web App to Home Screen / Desktop')}</span>
          </div>
        )}

        <div className={styles.spaceArea}>
          <div className={styles.greenBanner}> {t('Spaces')} </div>
          {recentSpacesData.length > 0 ? (
            <AllHorizontalRow
              sectionTitle={'Recent'}
              items={recentSpacesData}
              onClickAll={onClickRecentSpaces}
            >
              {(item: SpaceNodeProps) => <SpaceNode {...item} />}
            </AllHorizontalRow>
          ) : null}

          {/* {hotSpaces.length > 0 ? (
            <AllHorizontalRow
              sectionTitle={'HOT Spaces'}
              items={hotSpaces}
              onClickAll={onSeeAllHotClubs}
            >
              {(item: Space) => (
                <ClubViewCard
                  space={item}
                  width={173.5}
                  height={256}
                  backgroundColor="#F1F1F1"
                  onClick={() => onClickClub(item.classroomId)}
                />
              )}
            </AllHorizontalRow>
          ) : null} */}

          <div className={styles.whiteBar} onClick={onSeeAllHotClubs}>
            <div className={styles.iconContainer}>
              <SvgIcon
                name={SVGIconType.Classroom}
                width={28}
                height={28}
                color={'#333'}
              />
            </div>
            <div className={styles.whiteBarText}>{t('HOT Spaces')}</div>
            <div className={styles.whiteBarArrow}>
              <SvgIcon
                name={SVGIconType.Arrow}
                color="#808080"
                width={16}
                height={16}
              />
            </div>
          </div>
        </div>

        {/* <div className={styles.spaceArea}>
          <div className={styles.greenBanner}> {t('Web Store')} </div>
          <AllHorizontalRow
            sectionTitle={'Popular'}
            items={webProducts}
            onClickAll={onClickAllWebStore}
          >
            {(item: RewardOffer) => (
              <WebStoreProductNode
                rewardOffer={item}
                onClickProduct={() => {
                  onClickProduct(item.rewardOfferId);
                }}
              />
            )}
          </AllHorizontalRow>
        </div> */}

        <div className={`${styles.joyToTheWorldArea} joy-to-the-worldArea`}>
          <div className={styles.greenBanner}>
            {t('Your Contribution to the World')}
          </div>
          <div className={styles.orangeContainer} style={{ gap: '1rem' }}>
            <div className={styles.hungryContainer}>
              <div className={styles.hungryBoxContainer}>
                <div className={styles.hungryBoxContent}>
                  <img
                    src={Icons[IconType.Cry]}
                    className={styles.hungryBoxIcon}
                  />
                  <div className={styles.orangeBoxText}>
                    {t('We are so hungry!')}
                  </div>
                </div>
                <div className={styles.hungryBoxTriangle} />
              </div>

              <div className={styles.hungryImage}>
                <img
                  src={getDonationImage(donationImageSet)}
                  className={styles.slideContent}
                  alt=""
                />
              </div>
            </div>

            <p className={styles.normalText}>
              {t(
                'When you donate bread, JOYAJOY will donate the same amount of bread to hungry children in the real world.',
              )}
            </p>

            <hr
              style={{
                backgroundColor: 'white',
                height: '2px',
                opacity: '0.5',
              }}
            />

            <p style={{ fontSize: '1rem' }}>{t('You have:')}</p>

            <div
              className={styles.hContainer}
              style={{ gap: '1rem', marginTop: '-8px', marginBottom: '-4px' }}
            >
              <div className={styles.brownBoldText}>{t('Bread')}</div>
              <BreadBar
                breadSlice={breadSlice}
                height="20px"
                width="200px"
                iconSize={48}
                backgroundColor="#fff"
              />
            </div>
            <div
              className={styles.hContainer}
              style={{ justifyContent: 'space-evenly' }}
            >
              <div
                className={`${styles.getMoreButton} ${styles.hoverStyle}`}
                onClick={onClickGetMoreBread}
              >
                {t('Get More')}
              </div>
              <div
                className={`${styles.donateButton} ${styles.hoverStyle}`}
                onClick={onClickDonateBread}
              >
                {t('Donate')}
              </div>
            </div>

            {/* <div className={styles.whiteContainer}>
              <p className={styles.normalText}>
                {t('Total Amount of Bread You have Donated')}
              </p>
              <div
                className={styles.hContainer}
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.8rem',
                  gap: '5px',
                }}
              >
                <PngIcon name={'Bread'} width={32} height={32} />
                {Math.floor(profile?.statistics.donatedBreadCount ?? 0)}
                <SvgIcon
                  onClick={onClickBreadDonated}
                  name={SVGIconType.GameInstruction}
                  width={32}
                  height={32}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div> */}
          </div>
          <div className={styles.blueFooter}>
            <p style={{ color: '#808080' }}>{t('Total Bread Donated: ')}</p>
            <PngIcon name={'Bread'} width={24} height={24} />
            <span style={{ color: '#808080', fontWeight: 'bold' }}>
              {Math.floor(profile?.statistics.donatedBreadCount ?? 0)}
            </span>
            <SvgIcon
              onClick={onClickBreadDonated}
              name={SVGIconType.GameInstruction}
              width={24}
              height={24}
              style={{ cursor: 'pointer' }}
              color="#808080"
            />
          </div>
          {/* <div className={styles.greyContainer}>
            <p className={styles.normalText}>{t('You have:')}</p>
            <div className={styles.hContainer} style={{ gap: '1rem' }}>
              <div className={styles.brownBoldText}>{t('Bread')}</div>
              <BreadBar
                breadSlice={breadSlice}
                height="20px"
                width="200px"
                iconSize={48}
                backgroundColor="#fff"
              />
            </div>
            <div
              className={styles.hContainer}
              style={{ justifyContent: 'space-evenly' }}
            >
              <div
                className={`${styles.getMoreButton} ${styles.hoverStyle}`}
                onClick={onClickGetMoreBread}
              >
                {t('Get More')}
              </div>
              <div
                className={`${styles.donateButton} ${
                  breadSlice < 500 ? styles.disabledStyle : styles.hoverStyle
                }`}
                onClick={breadSlice < 500 ? undefined : onClickDonateBread}
              >
                {t('Donate')}
              </div>
            </div>
          </div> */}

          <div style={{ margin: '0 12px' }}>
            <hr />
          </div>
          <p className={styles.normalText}>
            {t(
              'When you share JOYAJOY, more people can help provide more bread to those in need.',
            )}
          </p>
          <p className={styles.normalText}>
            {t('Help us save more children from hunger.')}
          </p>

          <div
            className={`${styles.shareButton} ${styles.hoverStyle}`}
            onClick={onClickShare}
          >
            <SvgIcon
              name={SVGIconType.Share}
              width={24}
              height={24}
              color={'#ffffff'}
            />
            <div>{t('Share JOYAJOY')}</div>
          </div>
        </div>
        {/* 用户已点击按钮后显示在该位置 */}
        {!isAddHomeButtonOnTop && (
          <div
            className={`${styles.addToHomeScreenButton}`}
            onClick={onClickAddToHomeScreen}
          >
            <img
              className={styles.joyajoyicon}
              src={Icons[IconType.JoyajoyIcon]}
            />
            <span>{t('Add Web App to Home Screen / Desktop')}</span>
          </div>
        )}
      </div>
    </div>
  );
});
